import type { AxiosResponse } from 'axios'
import { clone } from 'lodash-es'
import type { RequestOptions, Result } from '/#/axios'
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform'
import { VAxios } from './Axios'
import { checkStatus } from './checkStatus'
import { useGlobSetting } from '/@/hooks/setting'
import { useMessage } from '/@/hooks/web/useMessage'
import { RequestEnum, ResultEnum, ContentTypeEnum } from '/@/enums/httpEnum'
import { isString } from '/@/utils/is'
import { getToken } from '/@/utils/auth'
import { setObjToUrlParams, deepMerge, toLogin } from '/@/utils'
import { useErrorLogStoreWithOut } from '/@/store/modules/errorLog'
import { useI18n } from '/@/hooks/web/useI18n'
import { joinTimestamp, formatRequestDate } from './helper'

const globSetting = useGlobSetting()
const urlPrefix = globSetting.urlPrefix
const { createMessage, createErrorModal } = useMessage()

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理请求数据。如果数据不是预期格式，可直接抛出错误
   */
  transformRequestHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    const { t } = useI18n()
    const { isTransformResponse, isReturnNativeResponse } = options
    // 错误的时候返回
    if (!res.data) {
      // return '[HTTP] Request has no return value';
      const error = new Error(t('sys.api.apiRequestFailed'))

      throw error
    }

    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      return res
    }
    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      return res.data
    }

    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    const { code, data, msg } = res.data
    // 这里逻辑可以根据项目进行修改
    const hasSuccess = +code === ResultEnum.SUCCESS
    if (hasSuccess) {
      return data
    }

    let timeoutMsg = ''

    switch (code) {
      case ResultEnum.TIMEOUT:
        timeoutMsg = t('sys.api.timeoutMessage')
        toLogin()
        break
      case ResultEnum.TOKEN_EXPIRED:
        timeoutMsg = msg
        toLogin()
        break
      default:
        timeoutMsg = msg || t('sys.api.apiRequestFailed')
    }

    if (options.errorMessageMode === 'modal') {
      createErrorModal({ title: t('sys.api.errorTip'), content: timeoutMsg })
    } else if (options.errorMessageMode === 'message' && !options.ignoreBusinessError) {
      createMessage.error({
        content: timeoutMsg,
        key: 'timeout-message',
      })
    }

    return Promise.reject(res.data)
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options

    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`
    }

    if (apiUrl && isString(apiUrl) && !config.url?.startsWith('http')) {
      config.url = `${apiUrl}${config.url}`
    }
    const params = config.params || {}
    const data = config.data || false
    formatDate && data && !isString(data) && formatRequestDate(data)
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false))
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`
        config.params = undefined
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params)
        if (Reflect.has(config, 'data') && config.data && Object.keys(config.data).length > 0) {
          config.data = data
          config.params = params
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params
          config.params = undefined
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data),
          )
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params
        config.params = undefined
      }
    }

    // 本地联调：GET 请求补 userId query。网关才会按 token 注入 userId（GET 补 query），
    // 本地 VITE_PROXY 直连后端绕过网关，缺 userId 时后端 dto.getUserId() 为 null，
    // 返回 20012「用户不存在」→ 详情页整片空白且前端无报错。
    if (
      import.meta.env.DEV &&
      import.meta.env.VITE_LOCAL_USER_ID &&
      config.method?.toUpperCase() === RequestEnum.GET
    ) {
      const url = config.url as string
      // 不覆盖调用方显式传入的 userId（如 getMasterById 传的是技师 userId，非当前登录人）
      if (url && !/[?&]userId=/.test(url)) {
        config.url = `${url}${url.includes('?') ? '&' : '?'}userId=${
          import.meta.env.VITE_LOCAL_USER_ID
        }`
      }
    }

    // 本地联调：补 X-USER-ID 请求头。网关按 token 注入该头，本地 VITE_PROXY 直连后端时缺失，
    // 后端 @RequestHeader("X-USER-ID") 取不到当前用户（如 third-push-log/retry 返回 20008「系统错误」）。
    if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_USER_ID) {
      const headers: any = config.headers || {}
      if (!headers['X-USER-ID'] && !headers['x-user-id']) {
        headers['X-USER-ID'] = import.meta.env.VITE_LOCAL_USER_ID
        config.headers = headers
      }
    }

    return config
  },

  /**
   * @description: 请求拦截器处理
   */
  requestInterceptors: (config, options) => {
    // 请求之前处理config
    const token = getToken()
    if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
      // jwt token
      ;(config as Recordable).headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme} ${token}`
        : token
    }
    // 本地联调：网关不注入 userId，通过环境变量手动补入 POST body
    if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_USER_ID) {
      const method = config.method?.toUpperCase()
      if (method !== 'GET' && config.data && typeof config.data === 'object') {
        config.data.userId = Number(import.meta.env.VITE_LOCAL_USER_ID)
      }
    }
    return config
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    return res
  },

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: async (error: any) => {
    console.log('responseInterceptorsCatch', error)
    const { t } = useI18n()
    const errorLogStore = useErrorLogStoreWithOut()
    errorLogStore.addAjaxErrorInfo(error)
    const { response, code, message, config } = error || {}
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none'
    const msg: string = response?.data?.error?.message ?? ''
    const err: string = error?.toString?.() ?? ''
    let errMessage = ''
    try {
      if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
        errMessage = t('sys.api.apiTimeoutMessage')
      }
      if (err?.includes('Network Error')) {
        errMessage = t('sys.api.networkExceptionMsg')
      }

      if (errMessage) {
        if (errorMessageMode === 'modal') {
          createErrorModal({ title: t('sys.api.errorTip'), content: errMessage })
        } else if (errorMessageMode === 'message') {
          createMessage.error({
            content: errMessage,
            key: 'err-message',
          })
          // createMessage.error(errMessage)
        }
        return Promise.reject(error)
      }
    } catch (e: any) {
      throw new Error(e as unknown as string)
    }

    checkStatus(error?.response?.status, msg, errorMessageMode)

    return Promise.reject(error)
  },
}

function createAxios(opt?: Partial<CreateAxiosOptions>) {
  return new VAxios(
    deepMerge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: '',
        timeout: 10 * 1000,
        // 基础接口地址
        // baseURL: globSetting.apiUrl,

        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: clone(transform),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          joinPrefix: true,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          // 接口地址
          apiUrl: globSetting.apiUrl,
          // 接口拼接地址
          urlPrefix: urlPrefix,
          //  是否加入时间戳
          joinTime: false,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
        },
      },
      opt || {},
    ),
  )
}
export const defHttp = createAxios()

// other api url
// export const otherHttp = createAxios({
//   requestOptions: {
//     apiUrl: 'xxx',
//     urlPrefix: 'xxx',
//   },
// });
